import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  ensureSupportRoom,
  sendConversationMessage,
  uploadConversationAttachment,
} from 'utils/messagingSupportHubApi';

const BugReportModal = ({ isOpen, onClose, defaultHar, onSuccess }) => {
  const [form, setForm] = useState({
    summary: '',
    expected: '',
    actual: '',
    include_logs: true,
    allow_contact: true,
    har: defaultHar || null,
  });
  const [loading, setLoading] = useState(false);

  const buildMessageBody = () => {
    const lines = [
      'Bug report',
      '',
      `Summary: ${form.summary || '(none)'}`,
      `Expected: ${form.expected || '(none)'}`,
      `Actual: ${form.actual || '(none)'}`,
      `Support may contact me: ${form.allow_contact ? 'yes' : 'no'}`,
    ];
    if (form.include_logs && form.har && typeof form.har === 'object' && form.har.name) {
      lines.push(`HAR file: ${form.har.name} (uploaded as attachment if applicable)`);
    } else if (form.include_logs) {
      lines.push('Technical logs (HAR): user opted in; attach a .har file when available.');
    } else {
      lines.push('Technical logs (HAR): not included.');
    }
    return lines.join('\n');
  };

  const submit = async () => {
    setLoading(true);
    try {
      const room = await ensureSupportRoom('bug-reports', { createNew: true });
      if (!room?.success) {
        throw new Error(room?.error || 'Could not open bug report conversation');
      }
      const conversationId = room.conversation_id || room.matrix_room_id;
      if (!conversationId) {
        throw new Error('Missing conversation id from support room response');
      }
      const msgRes = await sendConversationMessage(conversationId, buildMessageBody());
      if (!msgRes?.success) {
        throw new Error(msgRes?.error || 'Failed to send bug report message');
      }
      if (
        form.include_logs &&
        form.har &&
        typeof File !== 'undefined' &&
        form.har instanceof File
      ) {
        const up = await uploadConversationAttachment(conversationId, form.har);
        if (!up?.success) {
          console.error('Bug report HAR upload failed', up?.error);
        }
      }
      onSuccess && onSuccess({ conversation_id: conversationId });
      onClose();
    } catch (e) {
      alert(e.message || 'Failed to submit bug report');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <AlertTriangle size={20} />
            <h3>Report a Bug</h3>
          </div>
          <button type="button" className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="bug-report-lead">
            This opens a private support thread with B-esper. You can continue the conversation under Communications.
          </p>

          <div className="form-row">
            <div className="form-group">
              <label>Describe the problem</label>
              <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Please describe what went wrong…" />
            </div>
            <div className="form-group">
              <label>What did you expect to happen?</label>
              <textarea value={form.expected} onChange={(e) => setForm({ ...form, expected: e.target.value })} placeholder="Describe what should have happened…" />
            </div>
            <div className="form-group">
              <label>What actually happened?</label>
              <textarea value={form.actual} onChange={(e) => setForm({ ...form, actual: e.target.value })} placeholder="Describe what actually occurred…" />
            </div>
          </div>

          <div className="form-group">
            <label>HAR file (optional)</label>
            <input
              type="file"
              accept=".har,application/json"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                setForm((prev) => ({ ...prev, har: f || null }));
              }}
            />
          </div>

          <div className="form-group checkbox">
            <label>
              <input type="checkbox" checked={form.include_logs} onChange={(e) => setForm({ ...form, include_logs: e.target.checked })} />
              Include technical logs (upload HAR above when available)
            </label>
          </div>
          <div className="form-group checkbox">
            <label>
              <input type="checkbox" checked={form.allow_contact} onChange={(e) => setForm({ ...form, allow_contact: e.target.checked })} />
              Support may contact me if they need more details
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" onClick={onClose} disabled={loading}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'Submitting…' : 'Submit bug report'}
          </button>
        </div>
      </div>
      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 60; }
        .modal-content { width: 90%; max-width: 720px; background: var(--bg-primary); border-radius: 12px; border: 1px solid var(--border-light); overflow: hidden; }
        .modal-header { display:flex; align-items:center; justify-content: space-between; padding:16px 20px; border-bottom:1px solid var(--border-light); }
        .header-content { display:flex; align-items:center; gap:8px; font-weight:600; }
        .modal-body { padding: 16px 20px; }
        .modal-footer { padding: 16px 20px; border-top:1px solid var(--border-light); display:flex; justify-content:flex-end; gap:8px; }
        .close-button { background:none; border:none; font-size:18px; cursor:pointer; color: var(--text-secondary); }
        .form-row { display: grid; gap: 12px; }
        .form-group { display:flex; flex-direction:column; gap:6px; }
        .form-group.checkbox { flex-direction:row; align-items:center; gap:8px; }
        .bug-report-lead { margin: 0 0 12px; font-size: 14px; color: var(--text-secondary); line-height: 1.4; }
        textarea { min-height: 80px; resize: vertical; padding:10px; border-radius:8px; border:1px solid var(--border-light); background: var(--bg-secondary); color: var(--text-primary); }
        .btn { padding:8px 14px; border-radius:8px; border:1px solid var(--border-light); background: var(--bg-secondary); color: var(--text-primary); cursor:pointer; }
        .btn-primary { background: var(--primary); color: white; border-color: var(--primary); }
      `}</style>
    </div>
  );
};

export default BugReportModal;
